import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {  useParams } from "react-router-dom";

import { useTranslation } from "core/hooks/useTranslation";
import usePermissions from 'core/hooks/usePermission';
import useRole from 'core/hooks/useRole';

import TabTableDashboardLayout from "components/layouts/tab/table";
import Table from 'components/common/table';
import AddAffiliates from './addAffiliates';

import { tableColumnsCreator } from 'utils/tableColumnsCreator';
import flagsToBinary from 'core/helpers/common/flagsToBinary';

import { USER_ROLE, USER_STATE } from 'core/constants/auth';
import { PERMISSION_RESOURCE, PERMISSION_ACTION } from 'core/constants/permission';
import { columns } from './columns';

import useAffiliates from './useAffiliates';

import campaignService from 'core/services/apiServices/campaignService';

import autosuggestionService from 'core/services/apiServices/autosuggestionService';
import { useGlobalSelector, userInfoSelector } from 'core/stores/globalStore';

const ROW_UNIQUE_KEY_PROP = "longId";

const AffiliatesComponent = () => {
    const { t } = useTranslation();

    const permissionUtils = usePermissions();
    const { hasRole } = useRole();

    const searchParams = useParams();

    const [ isSaving , setIsSaving ] = useState(false)

    const campaignId = searchParams.id;
    const [ affiliatesAutosuggestion, setAffiliatesAutosuggestion ] = useState([])
    const [addPopupData, setAddPopupData] = useState({
        isVisible: false
    });

    const userInfo = useGlobalSelector(userInfoSelector);
    const isAffiliateManager = hasRole(USER_ROLE.AFFILIATE_MANAGER);
    const hasCampaignManagementAccess = userInfo.campaignManagement;

    const hasModifyPermission = isAffiliateManager ? hasCampaignManagementAccess : permissionUtils.has( PERMISSION_RESOURCE.MARKETING_TOOLS_CAMPAIGN, PERMISSION_ACTION.MODIFY )
   
    const {
        loading,
        affiliates,
        addAffiliates,
        removeAffiliate
    } = useAffiliates(campaignId);

    useEffect(() => {
        const stateBinaryCode = flagsToBinary([
            USER_STATE.ACTIVE,
            USER_STATE.EXPIRED,
            USER_STATE.LOCKED_2_FA,
            USER_STATE.LOCKED_LOGIN,
        ]);
        autosuggestionService.getAffiliates({ state: stateBinaryCode })
            .then(value => setAffiliatesAutosuggestion(value));
    }, [])

    const onAffiliateSave = useCallback(data => {
        addAffiliates(data)
    }, [addAffiliates])

    const handleAffiliateDelete = affiliate => {
        setIsSaving(true)
        campaignService.deleteAffiliate({
            id : campaignId,
            affiliateId: affiliate.id,
        })
            .then(() => removeAffiliate(affiliate.id))
            .finally(() => setIsSaving(false))
    }

    const showAddPopup = () => {
        setAddPopupData({
            isVisible: true
        })
    }

    const hideAddPopup = useCallback(() => {
        setAddPopupData({
            isVisible: false
        })
    }, [setAddPopupData])

    const availableAffiliates = useMemo(() => {
        const affiliateIds = affiliates.map(affiliate => affiliate.id)
        return affiliatesAutosuggestion
                    .filter(c => !affiliateIds.includes(c.id))
    }, [affiliatesAutosuggestion, affiliates])

    const { mainTableColumns } = useMemo(() => {
        return tableColumnsCreator({
            mainColumns: columns,
            t
        });
    }, [t])

    const tableRowActions = [];

    if (hasModifyPermission) {
        tableRowActions.push({
            title: t('backoffice.common.delete'),
            confirmationMessage: t("backoffice.campaigns.doYouWanttoDeleteAffiliate"),
            confirmationTitle: t("backoffice.campaigns.deleteAffiliate"),
            icon: "trash",
            showConfirmation: true,
            onClick: handleAffiliateDelete
        })
    }

    const headerPartsData = {
        button: {
            icon: "plus",
            type: "primary",
            onClick: () => showAddPopup(null),
            text: t("backoffice.campaigns.addAffiliates"),
            enabled: hasModifyPermission,
            disabled: availableAffiliates.length === 0
        }
    }



    return (
        <TabTableDashboardLayout
            header={headerPartsData}
        >
            <Table
                loading={loading || isSaving}
                columns={mainTableColumns}
                data={affiliates}
                uniqueKey={ROW_UNIQUE_KEY_PROP}
                disableFullView={true}
                actions={tableRowActions}
                noPagination={true}
            />

            <AddAffiliates
                isVisible={addPopupData.isVisible}
                availableAffiliates={availableAffiliates}
                onSave={onAffiliateSave}
                onClose={hideAddPopup}
            />
        </TabTableDashboardLayout>
    )
}

export default AffiliatesComponent;